import React, {
	createContext,
	ReactNode,
	useContext,
	useState,
	useEffect,
} from 'react'

import { OverlapDetails } from '../../utils/priorityCalculator'
import { CalendarEvent, initialEvents } from '../Data'

interface EventsContextType {
	events: CalendarEvent[]
	setEvents: (events: CalendarEvent[]) => void
	addEvent: (event: CalendarEvent) => void
	deleteEvent: (id: string) => void
	updateEvent: (id: string, updatedEvent: Partial<CalendarEvent>) => void
	eventViewOpen: boolean
	setEventViewOpen: (value: boolean) => void
	eventAddOpen: boolean
	setEventAddOpen: (value: boolean) => void
	eventEditOpen: boolean
	setEventEditOpen: (value: boolean) => void
	eventDeleteOpen: boolean
	setEventDeleteOpen: (value: boolean) => void
	availabilityCheckerEventAddOpen: boolean
	setAvailabilityCheckerEventAddOpen: (value: boolean) => void
	selectedEvent: CalendarEvent | null
	setSelectedEvent: (event: CalendarEvent | null) => void
	// Smart slot type selection states
	slotTypeModalOpen: boolean
	setSlotTypeModalOpen: (value: boolean) => void
	selectedSlotType: 'regular' | 'smart' | null
	setSelectedSlotType: (type: 'regular' | 'smart' | null) => void
	smartSlotTypeModalOpen: boolean
	setSmartSlotTypeModalOpen: (value: boolean) => void
	selectedSmartType: 'weather' | 'occupation' | null
	setSelectedSmartType: (type: 'weather' | 'occupation' | null) => void
	weatherSlotStepperOpen: boolean
	setWeatherSlotStepperOpen: (value: boolean) => void
	occupancySlotStepperOpen: boolean
	setOccupancySlotStepperOpen: (value: boolean) => void
	// Overlap confirmation modal states
	overlapConfirmationModalOpen: boolean
	setOverlapConfirmationModalOpen: (value: boolean) => void
	overlapDetails: OverlapDetails | null
	setOverlapDetails: (details: OverlapDetails | null) => void
	pendingSlotStart: Date | null
	setPendingSlotStart: (date: Date | null) => void
	pendingSlotEnd: Date | null
	setPendingSlotEnd: (date: Date | null) => void
}

const EventsContext = createContext<EventsContextType | undefined>(undefined)

export const useEvents = () => {
	const context = useContext(EventsContext)
	if (!context) {
		throw new Error('useEvents must be used within an EventsProvider')
	}
	return context
}

export const EventsProvider: React.FC<{
	children: ReactNode
	initialEvents?: CalendarEvent[]
}> = ({ children, initialEvents: providedInitialEvents }) => {
	const [events, _setEvents] = useState<CalendarEvent[]>(
		providedInitialEvents || initialEvents
	)
	const [eventViewOpen, setEventViewOpen] = useState(false)
	const [eventAddOpen, setEventAddOpen] = useState(false)
	const [eventEditOpen, setEventEditOpen] = useState(false)
	const [eventDeleteOpen, setEventDeleteOpen] = useState(false)
	const [availabilityCheckerEventAddOpen, setAvailabilityCheckerEventAddOpen] =
		useState(false)
	const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
	// Smart slot type selection states
	const [slotTypeModalOpen, setSlotTypeModalOpen] = useState(false)
	const [selectedSlotType, setSelectedSlotType] = useState<
		'regular' | 'smart' | null
	>(null)
	const [smartSlotTypeModalOpen, setSmartSlotTypeModalOpen] = useState(false)
	const [selectedSmartType, setSelectedSmartType] = useState<
		'weather' | 'occupation' | null
	>(null)
	const [weatherSlotStepperOpen, setWeatherSlotStepperOpen] = useState(false)
	const [occupancySlotStepperOpen, setOccupancySlotStepperOpen] =
		useState(false)
	// Overlap confirmation modal states
	const [overlapConfirmationModalOpen, setOverlapConfirmationModalOpen] =
		useState(false)
	const [overlapDetails, setOverlapDetails] = useState<OverlapDetails | null>(
		null
	)
	const [pendingSlotStart, setPendingSlotStart] = useState<Date | null>(null)
	const [pendingSlotEnd, setPendingSlotEnd] = useState<Date | null>(null)

	// Only set initial events once when component mounts
	// Don't overwrite local changes when providedInitialEvents changes
	useEffect(() => {
		if (
			providedInitialEvents &&
			providedInitialEvents.length > 0 &&
			events.length === 0
		) {
			_setEvents(providedInitialEvents)
		}
	}, [providedInitialEvents, events.length])

	const setEvents = (
		next: CalendarEvent[] | ((prev: CalendarEvent[]) => CalendarEvent[])
	) => {
		_setEvents(next as any)
	}

	const addEvent = (event: CalendarEvent) => {
		const newEvent = {
			...event,
			id: event.id || Date.now().toString(),
		}

		setEvents((prevEvents) => {
			const next = [...prevEvents, newEvent]
			return next
		})
	}

	const deleteEvent = (id: string) => {
		setEvents((prevEvents) => {
			const next = prevEvents.filter((event) => event.id !== id)
			return next
		})
	}

	const updateEvent = (id: string, updatedEvent: Partial<CalendarEvent>) => {
		setEvents((prevEvents) =>
			prevEvents.map((event) => {
				if (event.id !== id) return event

				const merged = { ...event, ...updatedEvent }

				if (updatedEvent.repeatDays !== undefined) {
					const isRecurring = updatedEvent.repeatDays.length > 0

					if (isRecurring) {
						return {
							...merged,
							start: undefined as any,
							end: undefined as any,
						}
					} else {
						return {
							...merged,
							rrule: undefined,
							duration: undefined,
							startTime: undefined as any,
						}
					}
				}

				return merged
			})
		)
	}

	return (
		<EventsContext.Provider
			value={{
				events,
				setEvents,
				addEvent,
				deleteEvent,
				updateEvent,
				eventViewOpen,
				setEventViewOpen,
				eventAddOpen,
				setEventAddOpen,
				eventEditOpen,
				setEventEditOpen,
				eventDeleteOpen,
				setEventDeleteOpen,
				availabilityCheckerEventAddOpen,
				setAvailabilityCheckerEventAddOpen,
				selectedEvent,
				setSelectedEvent,
				slotTypeModalOpen,
				setSlotTypeModalOpen,
				selectedSlotType,
				setSelectedSlotType,
				smartSlotTypeModalOpen,
				setSmartSlotTypeModalOpen,
				selectedSmartType,
				setSelectedSmartType,
				weatherSlotStepperOpen,
				setWeatherSlotStepperOpen,
				occupancySlotStepperOpen,
				setOccupancySlotStepperOpen,
				overlapConfirmationModalOpen,
				setOverlapConfirmationModalOpen,
				overlapDetails,
				setOverlapDetails,
				pendingSlotStart,
				setPendingSlotStart,
				pendingSlotEnd,
				setPendingSlotEnd,
			}}
		>
			{children}
		</EventsContext.Provider>
	)
}
